import { cn } from '@/lib/utils';
import { Dot } from 'lucide-react';
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Template } from '~/convex/schema';
import type { CheckIconHandle } from '../ui/check';
import { CheckIcon } from '../ui/check';

export const TemplateButton = ({
  template,
  isSelected,
  onSelect,
  centered = false,
}: {
  template: Template;
  isSelected: boolean;
  onSelect: () => void;
  centered?: boolean;
}) => {
  const { t } = useTranslation();
  const checkIconRef = useRef<CheckIconHandle>(null);

  useEffect(() => {
    if (isSelected) {
      checkIconRef.current?.startAnimation();
    } else {
      checkIconRef.current?.stopAnimation();
    }
  }, [isSelected]);

  return (
    <div
      className={cn(
        'ease-out-quad flex h-fit w-full cursor-pointer items-center gap-4 rounded-lg border px-5 py-4',
        centered ? 'justify-center' : 'justify-start',
        isSelected
          ? 'bg-primary/10 border-primary/30'
          : 'border bg-gray-100 hover:bg-gray-200/70',
      )}
      onClick={onSelect}>
      <div
        className={cn(
          'flex w-full flex-col gap-2',
          centered ? 'items-center' : 'items-start',
        )}>
        <div className="flex grow flex-row items-center justify-between gap-2">
          <h4 className="grow text-lg font-semibold text-gray-900">
            {template.name}
          </h4>
        </div>
        <span className="text-xs text-gray-500">
          <span className="text-primary shrink-0 text-sm font-semibold brightness-75">
            {template.language}
          </span>{' '}
          {' · '} {t('templates:section', { count: template.sections.length })}
        </span>
        {template.sections.length > 0 && (
          <div className="flex flex-row flex-wrap items-center justify-start gap-0.5">
            {template.sections.map((section) => {
              return (
                <Fragment key={section.title}>
                  <span className="text-sm" key={section.title}>
                    {section.title}
                  </span>
                  <Dot className="size-4 last:hidden" />
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
      <CheckIcon ref={checkIconRef} className="text-primary" size={24} />
    </div>
  );
};
